import { type FC, useMemo } from "react";
import { Navigate, generatePath, useParams } from "react-router-dom";

import { useRecoilValue } from "recoil";

import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { InsightsNotPermittedPage } from "../../core/components";
import { organizationsSelector } from "../../core/recoil/organizations";
import { AbsoluteRoute, type RouteParams } from "../../core/routes";
import { NavigateWithSearch } from "../../core/routes/NavigateWithSearch";
import { RouteEntities } from "../../core/routes/relativeRoutes";
import { CofConfigId, ResourceKind } from "../../gql-generated/types";
import { PageContentSpinner } from "../../layout/components";
import { useLens } from "../../lens/hooks";
import { useLensesOfSelectedContainer } from "../../lenses/hooks";
import { ROLE_ACTIONS } from "../../shared/constants";
import { useOrgChildResources, usePageRedirect, useSelectedOrganizationContainer } from "../../shared/hooks";
import { usePermittedBasedOnLensFolderRoleAction } from "../../shared/hooks/roles";
import { useDefaultOrganizationId } from "../../shared/hooks/useDefaultOrganizationId";
import { Feature } from "../../shared/models";
import { PaginationAtomsKeys } from "../../shared/state";
import { useLensIdSearchParameter, useTimeRange } from "../hooks";
import { LensGranularity } from "../models";
import {
    buildInsightsTabPath,
    getInsightsTabVisibilityMap,
    resolveVisibleInsightsTab,
} from "../utils/insightsNavigation";

import { LensInsightsHeader } from "./LensInsightsHeader";
import { LensInsightsTabs } from "./LensInsightsTabs";
import { LensInsightsPageContainer } from "./shared";

export const LensInsightsPage: FC = () => {
    const {
        insightsTab,
        orgId: routeOrgId,
        lensFolderId: routeLensFolderId,
    } = useParams<RouteParams[AbsoluteRoute.ORG_LENS_FOLDER_INSIGHTS_TAB]>();
    const isMonetizationTab = insightsTab === RouteEntities.Monetization;
    const { selectedTimeRange } = useTimeRange({
        defaultRange: isMonetizationTab ? LensGranularity.Lifetime : LensGranularity.Month,
    });
    const { lensFolderId, orgId } = useSelectedOrganizationContainer();
    const defaultOrganizationId = useDefaultOrganizationId();
    const { organizationsIndex } = useRecoilValue(organizationsSelector);
    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;
    const isMonetizationHubEnabled = configs[CofConfigId.MyLensesMonetizationInsights].enabled;

    const selectedOrganization = organizationsIndex.get(orgId ?? "");

    const { resourceContainers: lensFolders, loading: isLensFoldersLoading } = useOrgChildResources({
        kind: ResourceKind.LensSpaces,
    });

    const { isPermitted: isPermittedToViewInsights, loading: isPermittedToViewInsightsLoading } =
        usePermittedBasedOnLensFolderRoleAction(lensFolderId ?? "", ROLE_ACTIONS.READ_LENS_INSIGHTS);

    const shouldShowInsightsForContainer = !lensFolderId || isPermittedToViewInsights;

    const lensesOfSelectedContainerResult = useLensesOfSelectedContainer(
        PaginationAtomsKeys.LensesInsightsHeader,
        isMonetizationTab,
    );
    const { lenses, isFirstFetching, isReFetching } = lensesOfSelectedContainerResult;
    let isEmptyOrganization = !!selectedOrganization && selectedOrganization.lensSpacesList.length === 0;

    isEmptyOrganization = isMyLensesOrgsV2Enabled ? lensFolders.length === 0 : isEmptyOrganization;
    const lensesLoading = !isEmptyOrganization && (isFirstFetching || isReFetching);

    const { lensId } = useLensIdSearchParameter();
    const { data, loading: isLensDataLoading } = useLens(lensId ?? "", !lensId);
    const shouldShowAggregatedInsights = orgId === defaultOrganizationId;
    const isAggregatedInsightsSelected = shouldShowAggregatedInsights && !lensFolderId;

    const selectedLens = data?.getLens.lens;

    const doesBelongToContainer =
        !lensId || !selectedLens || !lensFolderId || selectedLens.lensAccountId?.lensSpaceId === lensFolderId;

    const shouldSelectFirstLens =
        !shouldShowAggregatedInsights && lenses.length > 0 && !lensId && doesBelongToContainer;

    const tabVisibilityMap = useMemo(
        () =>
            getInsightsTabVisibilityMap({
                selectedLens,
                selectedTimeRange,
                isMonetizationHubEnabled,
            }),
        [selectedLens, selectedTimeRange, isMonetizationHubEnabled],
    );
    const resolvedInsightsTab = useMemo(
        () =>
            resolveVisibleInsightsTab({
                currentTab: insightsTab,
                tabVisibilityMap,
            }),
        [insightsTab, tabVisibilityMap],
    );

    const redirect = usePageRedirect(Feature.insights);
    const selectedLensOrgId = selectedLens?.organizationId;
    const selectedLensFolderId = selectedLens?.lensAccountId?.lensSpaceId;
    const needsLensFolderRedirect = lensId && !lensFolderId && selectedLensOrgId && selectedLensFolderId;

    if (needsLensFolderRedirect) {
        return (
            <NavigateWithSearch
                to={generatePath(AbsoluteRoute.ORG_LENS_FOLDER_INSIGHTS_TAB, {
                    orgId: selectedLensOrgId,
                    lensFolderId: selectedLensFolderId,
                    insightsTab: insightsTab ?? RouteEntities.Audience,
                })}
                replace
            />
        );
    }

    if (redirect && !(lensId && isLensDataLoading)) {
        return <NavigateWithSearch to={redirect} />;
    }

    if (shouldSelectFirstLens && !isMonetizationTab) {
        const urlParams = new URLSearchParams();
        urlParams.set("lensId", lenses[0]?.id ?? "");
        const target = `${generatePath(AbsoluteRoute.ORG_LENS_FOLDER_INSIGHTS_TAB, {
            orgId: orgId ?? "",
            lensFolderId: lensFolderId ?? "",
            insightsTab: insightsTab ?? RouteEntities.Audience,
        })}?${urlParams.toString()}`;
        return <Navigate to={target} replace />;
    }

    if (
        (!isMonetizationTab && (isLensDataLoading || !doesBelongToContainer)) ||
        (!isMonetizationTab && !shouldShowAggregatedInsights && lensesLoading) ||
        isPermittedToViewInsightsLoading ||
        isLensFoldersLoading
    ) {
        return <PageContentSpinner />;
    }

    if (!shouldShowInsightsForContainer) {
        return <InsightsNotPermittedPage />;
    }

    // Self-heal URLs that point to a tab hidden by the current lens/time range context.
    if (insightsTab !== resolvedInsightsTab) {
        return (
            <NavigateWithSearch
                to={buildInsightsTabPath({
                    orgId: routeOrgId,
                    lensFolderId: routeLensFolderId,
                    insightsTab: resolvedInsightsTab,
                })}
                replace
            />
        );
    }

    return (
        <LensInsightsPageContainer>
            <LensInsightsHeader
                hideAllLensesLink={!!lensFolderId}
                showAggregatedInsights={shouldShowAggregatedInsights}
                hideLensSearch={isMonetizationTab || isAggregatedInsightsSelected || isEmptyOrganization}
                selectedLens={selectedLens ?? undefined}
                hideFolderSelectionDropdown={isMonetizationTab || isEmptyOrganization}
                {...lensesOfSelectedContainerResult}
            />
            <LensInsightsTabs
                tabVisibilityMap={tabVisibilityMap}
                notEnoughData={!isAggregatedInsightsSelected && (lenses.length === 0 || isEmptyOrganization)}
            />
        </LensInsightsPageContainer>
    );
};
